import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import ArrowRightIcon from 'assets/v2/ArrowRightIcon';
import CircleIcon from 'assets/v2/CircleIcon';
import { CheckCircleIcon } from 'assets/v2/icons';
import Tooltip from 'components/v2/Tooltip';
import { COLORS } from 'theme';

import {
  CheckIconContainer,
  ChevronContainer,
  IconContainer,
  StepItem,
  StepLabel,
  StepperContainer,
} from './FormStepper.styles';

/**
 * Renders a stepper component for multi-step forms, displaying the current step and progress.
 * Supports both full and condensed stepper modes.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {number} props.currentStep - The index of the current step (0-based).
 * @param {boolean} props.condensedStepper - Whether to display a condensed version of the stepper.
 * @param {number} [props.condensedStepperSize] - Number of steps to show in condensed mode.
 * @param {boolean} [props.disabledStepIds] - Array of step IDs to disable.
 * @param {array} [props.steps] - Array of steps.
 * @param {boolean} [props.isLoading] - Whether the stepper is in a loading state.
 * @returns {JSX.Element} The rendered stepper component.
 */

const FormStepper = ({
  currentStep,
  condensedStepper,
  condensedStepperSize = 3,
  disabledStepIds = [],
  onPreviousStepClick,
  steps,
  isLoading = false,
}) => {
  const [startStepIndex, setStartStepIndex] = useState(0);
  const [endStepIndex, setEndStepIndex] = useState(steps.length - 1);

  useEffect(() => {
    /**
    Calculates the visible step range for condensed stepper mode

    In condensed mode, this logic determines which steps should be visible:
    - Shows a window of steps centered around the current step
    - Maintains a fixed window size (condensedStepperSize)
    - Adjusts window position when reaching boundaries
    
    Example with condensedStepperSize = 3:
    - At step 1: Shows steps [0,1,2]
    - At step 4: Shows steps [3,4,5]
    - At last step: Shifts window to show last possible range
    */
    if (condensedStepper) {
      let startIndex = Math.max(0, currentStep - 1);
      let endIndex = startIndex + condensedStepperSize - 1;
      if (endIndex >= steps.length) {
        endIndex = steps.length - 1;
        startIndex = endIndex - condensedStepperSize + 1;
      }
      setStartStepIndex(startIndex);
      setEndStepIndex(endIndex);
    } else {
      setStartStepIndex(0);
      setEndStepIndex(steps.length - 1);
    }
  }, [condensedStepper, currentStep, condensedStepperSize, steps]);

  const handleStepClick = useCallback(
    (stepIndex) => {
      if (stepIndex < currentStep) {
        onPreviousStepClick(stepIndex);
      }
    },
    [currentStep, onPreviousStepClick],
  );

  const renderStep = useCallback(
    (step, stepIndex) => {
      const isCompleted = stepIndex < currentStep;
      const isCurrent = stepIndex === currentStep;
      const isLastStep = stepIndex === endStepIndex;
      const isDisabled = disabledStepIds.includes(step.id) || isLoading;

      const stepContent = (
        <StepItem
          $isFilled={isCompleted}
          $isHighlighted={isCurrent}
          $disabled={isDisabled}
          key={step.id}
          onClick={() => {
            if (!isDisabled) {
              handleStepClick(stepIndex);
            }
          }}
        >
          {isCompleted && !isDisabled ? (
            <CheckIconContainer>
              <CheckCircleIcon />
            </CheckIconContainer>
          ) : (
            <IconContainer $isHighlighted={isCurrent} $disabled={isDisabled}>
              <CircleIcon />
            </IconContainer>
          )}
          <StepLabel>{step.label}</StepLabel>
        </StepItem>
      );

      return (
        <>
          {isDisabled ? (
            <Tooltip
              title={step.disabledStepTooltipTitle || 'This step is disabled'}
              placement="top"
              arrow
            >
              {stepContent}
            </Tooltip>
          ) : (
            stepContent
          )}
          {!isLastStep && (
            <ChevronContainer>
              <ArrowRightIcon />
            </ChevronContainer>
          )}
        </>
      );
    },
    [currentStep, endStepIndex, disabledStepIds, isLoading],
  );

  /**
   * Memoized array of rendered step components for the current visible range in the stepper.
   *
   * Uses `TRIP_CREATION_STEPS` to select steps from `startStepIndex` to `endStepIndex` (inclusive),
   * and maps each step to its rendered component using `renderStep`.
   *
   * @type {React.ReactNode[]}
   */
  const displayableSteps = useMemo(() => {
    const displayedSteps = [];

    if (startStepIndex > 0) {
      displayedSteps.push(
        <>
          <MoreHorizIcon
            sx={{
              color: COLORS.black30,
            }}
          />
          <ChevronContainer>
            <ArrowRightIcon />
          </ChevronContainer>
        </>,
      );
    }

    steps.slice(startStepIndex, endStepIndex + 1).forEach((step, idx) => {
      displayedSteps.push(renderStep(step, idx + startStepIndex));
    });

    if (endStepIndex < steps.length - 1) {
      displayedSteps.push(
        <>
          <ChevronContainer>
            <ArrowRightIcon />
          </ChevronContainer>
          <MoreHorizIcon
            sx={{
              color: COLORS.black30,
            }}
          />
        </>,
      );
    }

    return displayedSteps;
  }, [renderStep, startStepIndex, endStepIndex]);

  return <StepperContainer>{displayableSteps}</StepperContainer>;
};

FormStepper.propTypes = {
  currentStep: PropTypes.number.isRequired,
  condensedStepper: PropTypes.bool,
  condensedStepperSize: PropTypes.number,
  disabledStepIds: PropTypes.array,
  onPreviousStepClick: PropTypes.func,
  steps: PropTypes.array,
  isLoading: PropTypes.bool,
};

export default memo(FormStepper);
